import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { Phone, Loader2, ArrowRight, ShieldCheck } from 'lucide-react';
import { buildBridgeLead } from '@/lib/eventsPortal';
import ConsentChecklist from '@/components/site/ConsentChecklist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  clinic_name: '',
  notes: '',
  privacy_consent: false,
  email_newsletter_consent: false,
  whatsapp_newsletter_consent: false,
};

const TXT = {
  it: {
    title: 'Ti richiamiamo noi',
    help: 'Lascia i tuoi contatti: ti chiamiamo entro 24 ore lavorative per concordare insieme un orario.',
    firstName: 'Nome *', lastName: 'Cognome *', email: 'Email *', phone: 'Telefono *',
    clinic: 'Studio / clinica (opzionale)',
    notes: 'Cosa vorresti capire dalla call? (opzionale)',
    notesPh: 'Es. quanto tempo serve per la migrazione, formazione del team, percorso FSE 2.0…',
    submit: 'Richiedi di essere richiamato',
    submitting: 'Invio in corso…',
    success: 'Perfetto! Ti ricontatteremo entro 24h.',
    successTitle: 'Richiesta ricevuta',
    successDesc: 'Ti chiamiamo entro 24 ore lavorative al numero che ci hai lasciato per concordare insieme un orario.',
    again: 'Invia un\'altra richiesta',
    error: 'Qualcosa non ha funzionato. Riprova o scrivici a info@primevision.it',
    required: 'Campo obbligatorio',
    invalidEmail: 'Email non valida',
    trust: 'Confidenziale · solo per il team Prime Vision · GDPR',
  },
  en: {
    title: 'We call you back',
    help: 'Leave your details: we call you within 24 working hours to agree on a time together.',
    firstName: 'First name *', lastName: 'Last name *', email: 'Email *', phone: 'Phone *',
    clinic: 'Practice / clinic (optional)',
    notes: 'What would you like to learn from the call? (optional)',
    notesPh: 'E.g. migration time, team training, FSE 2.0 flow…',
    submit: 'Request a callback',
    submitting: 'Sending…',
    success: 'Perfect! We\'ll contact you within 24h.',
    successTitle: 'Request received',
    successDesc: 'We\'ll call you within 24 working hours at the phone number you provided to agree on a time together.',
    again: 'Send another request',
    error: 'Something went wrong. Please retry or write to info@primevision.it',
    required: 'Required field',
    invalidEmail: 'Invalid email',
    trust: 'Confidential · Prime Vision team only · GDPR',
  },
};

export const CallbackForm = () => {
  const { locale } = useLanguage();
  const c = TXT[locale] || TXT.it;
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const onChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = c.required;
    if (!form.last_name.trim()) e.last_name = c.required;
    if (!form.email.trim()) e.email = c.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = c.invalidEmail;
    if (!form.phone.trim()) e.phone = c.required;
    if (!form.privacy_consent) e.privacy_consent = c.required;
    if (!form.email_newsletter_consent) e.email_newsletter_consent = c.required;
    if (!form.whatsapp_newsletter_consent) e.whatsapp_newsletter_consent = c.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error(c.error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bridge/call`, buildBridgeLead(form, 'call', {
        clinic_name: form.clinic_name.trim() || undefined,
        notes: form.notes.trim() || undefined,
        locale,
      }));
      setDone(true);
      toast.dismiss();
      toast.success(c.success);
    } catch (err) {
      console.error(err);
      toast.error(c.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="pv-bookcall-success"
        className="relative bg-white rounded-3xl p-8 md:p-12 border border-emerald-300 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.20)] overflow-hidden"
      >
        <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="w-16 h-16 rounded-2xl bg-emerald-100 border border-emerald-300 flex items-center justify-center mb-5">
            <Phone className="w-7 h-7 text-emerald-600" />
          </div>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-700 mb-2 font-mono">
            {c.successTitle}
          </div>
          <h3 className="font-heading font-bold text-pv-ink text-[26px] md:text-[30px] leading-tight">
            {c.success}
          </h3>
          <p className="mt-4 text-slate-600 text-[14.5px] leading-relaxed max-w-md">{c.successDesc}</p>
          <button
            onClick={() => { setDone(false); setForm(initial); }}
            data-testid="pv-bookcall-reset"
            className="mt-6 text-[13px] font-semibold text-slate-500 hover:text-pv-teal transition-colors"
          >
            {c.again}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      data-testid="pv-bookcall-callback-form"
      className="relative bg-white rounded-3xl p-6 md:p-8 border border-slate-200 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.20)] overflow-hidden"
    >
      <div className="absolute -top-32 -right-20 w-64 h-64 rounded-full bg-pv-teal/12 blur-3xl pointer-events-none" />
      <div className="relative mb-5">
        <div className="flex items-center gap-2 mb-2">
          <Phone className="w-4 h-4 text-pv-teal" />
          <h3 className="font-heading font-bold text-pv-ink text-[16px]">{c.title}</h3>
        </div>
        <p className="text-[13px] text-slate-600 leading-snug">{c.help}</p>
      </div>
      <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="cb_first_name" label={c.firstName} testId="pv-bookcall-first-name" value={form.first_name} onChange={onChange('first_name')} error={errors.first_name} />
        <Field id="cb_last_name" label={c.lastName} testId="pv-bookcall-last-name" value={form.last_name} onChange={onChange('last_name')} error={errors.last_name} />
        <Field id="cb_email" label={c.email} type="email" testId="pv-bookcall-email" value={form.email} onChange={onChange('email')} error={errors.email} />
        <Field id="cb_phone" label={c.phone} type="tel" testId="pv-bookcall-phone" value={form.phone} onChange={onChange('phone')} error={errors.phone} />
        <div className="sm:col-span-2">
          <Field id="cb_clinic" label={c.clinic} testId="pv-bookcall-clinic" value={form.clinic_name} onChange={onChange('clinic_name')} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cb_notes" className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2 font-bold">
            {c.notes}
          </label>
          <textarea
            id="cb_notes"
            data-testid="pv-bookcall-notes"
            rows={3}
            placeholder={c.notesPh}
            value={form.notes}
            onChange={onChange('notes')}
            className="pv-input resize-none"
          />
        </div>
        <div className="sm:col-span-2 mt-1">
          <ConsentChecklist form={form} onChange={onChange} errors={errors} testIdPrefix="pv-bookcall" />
        </div>
      </div>
      <div className="relative mt-6 flex flex-col sm:flex-row items-center gap-4">
        <button
          type="submit"
          data-testid="pv-bookcall-submit"
          disabled={submitting}
          className="pv-btn-primary group w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {c.submitting}
            </>
          ) : (
            <>
              <Phone className="w-4 h-4" />
              {c.submit}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </>
          )}
        </button>
        <div className="flex items-center gap-2 text-[12.5px] text-slate-500 font-medium">
          <ShieldCheck className="w-4 h-4 text-pv-teal" />
          {c.trust}
        </div>
      </div>
    </form>
  );
};

const Field = ({ id, label, type = 'text', testId, value, onChange, error }) => (
  <div className="flex flex-col">
    <label htmlFor={id} className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2 font-bold">
      {label}
    </label>
    <input id={id} type={type} data-testid={testId} value={value} onChange={onChange} className={`pv-input ${error ? 'border-red-400' : ''}`} />
    {error && <p className="mt-1.5 text-[11.5px] text-red-500 font-semibold">{error}</p>}
  </div>
);

export default CallbackForm;
